import React, { useEffect, useRef, useState } from "react";
import { useUserModel } from "../userModel";
import { YHDrawer } from "@components/YhDrawer";
import { FormItemTypeEnum, ISelectOption, IYhForm, IYhFormRef, IFormItem } from "@components/YhFormBox/Types";
import { YhForm } from "@components/YhFormBox";
import { GenderDs } from "@src/types/FmsDs";
import { globalPrompt } from "@src/components/YhMessage";
import { userService } from "../UserService";
import moment from "moment";
import useGlobalModel, { getFindItem } from "@src/model/globalModel";
import { IMdUserFields } from "../UserTypes";
import { forFind } from "@src/utils/utils";
import { message } from "antd";
import { useLockFn } from "ahooks";


export function DetailDrawer() {
    const {
        addressDs,
        roleDs,
        orgDs,
        shopDs,
        userTypeDs,
        drawerVisible,
        setDrawerVisible,
        selectedRow,
        editType,
        dataSource,
        loadData,
    } = useUserModel();
    const { user: { userName } } = useGlobalModel();

    const onCancel = () => {
        setDrawerVisible(false);
    }
    const [newOrgList, setNewOrgList] = useState<any>([]);

    const formRef = useRef<IYhFormRef>();

    // 处理表单值变化
    const handleValuesChange = async (changedValues: any) => {
        if ('orgCode' in changedValues) {
            const newOrgCode = changedValues.orgCode;
            formRef.current?.setFieldsValue({ depCode: '', depName: '' })
            await fetchOrgData(newOrgCode);

        }
    }

    // 提取为独立函数，接收orgCode参数
    const fetchOrgData = async (orgCode: string) => {
        if (!orgCode) {
            setNewOrgList([]);
            return;
        }
        let name = forFind(orgDs ?? [], orgCode as string, "value")?.title ?? "";
        let newOrgDs: any = orgDs ?? [];
        let newList: any = newOrgDs[0].children.find(
            (item: any) => item.title === name
        );
        setNewOrgList(newList.children ? newList.children : [])
    };

    // 初始化时获取一次orgCode
    useEffect(() => {
        const initOrgCode = async () => {
            if (formRef.current) {
                const values = await formRef.current.getFieldsValue();
                if (values.orgCode) {
                    await fetchOrgData(values.orgCode);
                }
            }
        };
        const setVal = async () => {
            formRef.current?.setFieldsValue(editType === 'add' ? {
                createTime: moment().format("YYYY-MM-DD HH:mm:ss"),
                createUser: userName,
                gender: 1,
                status: true
            } : {
                ...selectedRow,
                createTime: moment(selectedRow?.createTime).format("YYYY-MM-DD HH:mm:ss"),
                birthday: selectedRow?.birthday != null ? moment(selectedRow?.birthday ?? "" as string) : null,
                shops: selectedRow?.shops?.map(item => item.code),
                roles: selectedRow?.roles?.map(item => item.code),
                status: selectedRow?.status === 0,
                depName: selectedRow?.depName ? selectedRow?.depName.split(',') : undefined,
                depCode: selectedRow?.depCode ? selectedRow?.depCode.split(',') : undefined,
            })
        };
        if (drawerVisible) {
            setVal()
            initOrgCode();
        }
    }, [drawerVisible]); // 当抽屉显示时初始化

    // 失焦查询用户是否存在
    const staffBlur = async () => {
        const { staffId } = await formRef.current?.getFieldsValue();
        const isToched = await formRef.current?.isFieldTouched("staffId");
        if (editType === "edit") {
            let currentStaffId: string = dataSource.find(item => item?.code === selectedRow?.code)?.staffId as string;
            if (currentStaffId === staffId) return;
        }
        if (!staffId || !isToched) return;
        const { retData } = await userService.findStaff(staffId);
        if (!retData) {
            formRef.current?.setFieldsValue({
                staffId: ""
            });
            message.warning("您输入的员工工号已被占用");
        }
    }

    const userCodeBlur = async () => {
        const { code } = await formRef.current?.getFieldsValue();
        const isToched = await formRef.current?.isFieldTouched("code");
        if (!code || !isToched) return;
        const { retData } = await userService.findUserCode(code);
        if (!retData) {
            formRef.current?.setFieldsValue({
                code: ""
            });
            message.warning("您输入的账号已被占用");
        }
    }

    const formConfig: IYhForm = {
        formRef,
        labelCol: { span: 6 },
        disabled: editType === 'look',
        initialValues: formRef.current?.getFieldsValue(),
        justify: 'start',
        onChange: handleValuesChange, // 添加值变化回调
        items: [
            {
                type: FormItemTypeEnum.Input,
                fieldLabel: "员工工号",
                fieldName: "staffId",
                onInputBlur: staffBlur,
                span: 12,
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "账号",
                fieldName: "code",
                disable: editType === 'edit',
                span: 12,
                onInputBlur: userCodeBlur,
                rules: [{ required: true, message: "该项为必选项" }],
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "姓名",
                fieldName: "name",
                span: 12,
                rules: [{ required: true, message: "该项为必选项" }],
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "联系电话",
                fieldName: "phoneNumber",
                span: 12,
                rules: [{ required: true, message: "该项为必选项" }],
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "岗位",
                fieldName: "job",
                span: 12,
            }, {
                type: FormItemTypeEnum.Select,
                fieldLabel: "性别",
                fieldName: "gender",
                span: 12,
                ds: GenderDs
            }, {
                type: FormItemTypeEnum.Select,
                fieldLabel: "联系地址",
                fieldName: "address",
                span: 12,
                ds: addressDs
            },
            {
                type: FormItemTypeEnum.Datepicker,
                format: "YYYY-MM-DD",
                fieldLabel: "入职日期",
                fieldName: "birthday",
                allowClear: true,
                span: 12,
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "邮箱地址",
                fieldName: "email",
                span: 12,
            }, {
                type: FormItemTypeEnum.TreeSelect,
                fieldLabel: "所属机构",
                fieldName: "orgCode",
                span: 12,
                rules: [
                    { required: true, message: "请选择所属机构" }
                ],
                treeDs: orgDs
            }, {
                type: FormItemTypeEnum.TreeSelect,
                fieldLabel: "部门",
                fieldName: "depCode",
                multiple: true,
                treeDs: newOrgList,
                span: 12,
            },
            {
                type: FormItemTypeEnum.Switch,
                fieldLabel: "状态",
                fieldName: "status",
                span: 12,
            },
            ...(() => {
                return editType === "edit" ? [{
                    type: FormItemTypeEnum.Select,
                    fieldLabel: "用户类型",
                    fieldName: "userTypeCode",
                    span: 12,
                    ds: userTypeDs,
                    rules: [{ required: true, message: "该项为必选项" }],
                }, {
                    type: FormItemTypeEnum.Select,
                    mode: "multiple",
                    fieldLabel: "所属站点",
                    fieldName: "shops",
                    span: 12,
                    ds: shopDs,
                    filterOption: (inputValue: string, option: ISelectOption) => option.label.includes(inputValue),
                }, {
                    type: FormItemTypeEnum.Select,
                    mode: "multiple",
                    fieldLabel: "所属角色",
                    fieldName: "roles",
                    span: 12,
                    ds: roleDs,
                    filterOption: (inputValue: string, option: ISelectOption) => option.label.includes(inputValue),
                }] as IFormItem[] : [{
                    type: FormItemTypeEnum.Select,
                    fieldLabel: "用户类型",
                    fieldName: "userTypeCode",
                    span: 12,
                    ds: userTypeDs,
                    rules: [{ required: true, message: "该项为必选项" }],
                }]
            })(),
            {
                type: FormItemTypeEnum.Input,
                fieldLabel: "创建人",
                fieldName: "createUser",
                disable: true,
                span: 12,
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "创建时间",
                fieldName: "createTime",
                disable: true,
                span: 12,
            },
        ]
    };

    const onOk = useLockFn(async () => {
        if (editType === 'look') return onCancel()
        try {
            const formData = await formRef.current?.validateFields();
            if (formData) {
                const params: any = {
                    ...formData,
                    staffId: formData.staffId?.trim(),
                    birthday: formData.birthday ? moment(formData.birthday).format("YYYY-MM-DD") : undefined,
                    status: formData.status ? 0 : 1,
                    userTypeName: userTypeDs?.find(item => item.value === formData.userTypeCode)?.label ?? "",
                    orgName: forFind(orgDs ?? [], formData.orgCode as string, "value")?.title ?? "",
                    // depName: forFind(orgDs ?? [], formData.depCode as string, "value")?.title ?? "",
                }
                params.depName = formData.depCode ? formData.depCode.map((item: any) => forFind(orgDs ?? [], item, "value")?.title).toString() : null;
                params.depCode = formData.depCode ? formData.depCode.toString() : null;
                if (editType !== "edit") {
                    let { createTime, ...props } = params as IMdUserFields;
                    const insertRet = await userService.insert({
                        ...props,
                    });
                    if (insertRet.success) {
                        await globalPrompt("message", { type: "success", text: '新增成功!' });
                        setDrawerVisible(false)
                        loadData({ pageNum: 1 }, {})
                    } else {
                        await globalPrompt("message", { type: "error", text: "新增失败!" + insertRet.retMsg });
                    }
                } else {
                    const updateRet = await userService.updateLocal(params);
                    if (updateRet.success) {
                        await globalPrompt("message", { type: "success", text: '修改成功!' });
                        setDrawerVisible(false)
                        loadData({ pageNum: 1 })
                    } else {
                        await globalPrompt("message", { type: "error", text: "修改失败!" + updateRet.retMsg });
                    }
                }
            }
        } catch (e) {
            console.log("失败!");
        }
    })

    return (
        <YHDrawer
            {...{
                title: "用户信息",
                visible: drawerVisible,
                onOk,
                onCancel,
                width: 800,
                showFooter: true,
                okText: '保存',
                cancelText: '取消',
            }}
        >
            <YhForm  {...formConfig} />
        </YHDrawer>
    )
}